import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  styled,
  Button,
  Pagination,
  Stack,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookCard from "../../../components/cards/BookCard";
import FilterSection from "./FilterSection";
import SortDropdown from "./SortDropdown";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import useAudioBookList from "../../../hooks/api-request/useAudioBookList";
import BookListSkeleton from "../../../components/skeletons/BookListSkeleton";
import EmptyState from "../../../components/no-data/NoData";

const FilterButton = styled(Button)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
}));

const ResultCount = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 500,
  marginBottom: "24px",
}));

// Custom styled pagination for better RTL support
const StyledPagination = styled(Pagination, {
  shouldForwardProp: (prop) => prop !== "isRtl"
})(({ theme, isRtl }) => ({
  "& .MuiPaginationItem-root": {
    margin: isRtl ? "0 0 0 4px" : "0 4px 0 0",
  },
  "& .MuiPaginationItem-icon": {
    transform: isRtl ? "rotate(180deg)" : "rotate(0deg)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiPaginationItem-root": {
      minWidth: 30,
      height: 30,
      fontSize: "0.8rem",
    }
  }
}));

const AudioBookList = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  
  // Check if the language is RTL (Pashto or Dari)
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  // Get search query from URL
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search") || "";

  const [filters, setFilters] = useState({
    format: null,
    category: null,
    minPrice: null,
    maxPrice: null,
    search: searchQuery, // Initialize with URL search query
  });
  const [sortBy, setSortBy] = useState("-publication_date");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAudioBookList(filters, sortBy, page);
  const books = data?.results || [];
  const totalPages = data?.total_pages || 1;

  // Update filters when URL search parameter changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: searchQuery,
    }));
    setPage(1); // Reset to first page when search changes
  }, [searchQuery]);

  const handleFilterChange = (newFilters) => {
    // Update URL with search parameter if it exists
    if (newFilters.search) {
      searchParams.set("search", newFilters.search);
    } else {
      searchParams.delete("search");
    }
    navigate({ search: searchParams.toString() });

    setFilters(newFilters);
    setPage(1);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleClearFilters = () => {
    // Clear search from URL
    searchParams.delete("search");
    navigate({ search: searchParams.toString() });

    setFilters({
      format: null,
      category: null,
      minPrice: null,
      maxPrice: null,
      search: "",
    });
    setPage(1);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl">
        {/* Show active search query if exists */}
        {searchQuery && (
          <Box sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t("audioBooks.list.searchResults", { query: searchQuery })}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3} dir={isRtl ? 'rtl' : 'ltr'}>
          {/* Desktop Filter Section */}
          <Grid item md={3} sx={{ display: { xs: "none", md: "block" } }}>
            <FilterSection
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {/* Mobile Filter Button */}
            <FilterButton
              variant="outlined"
              onClick={() => setShowMobileFilter(true)}
            >
              <FilterAltIcon /> {t("audioBooks.list.filters")}
            </FilterButton>

            {/* Results Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <ResultCount>{data?.count || 0} {t("audioBooks.list.booksFound")}</ResultCount>
              <SortDropdown value={sortBy} onChange={handleSortChange} />
            </Box>

            {/* Books Grid */}
            {isLoading ? (
              <BookListSkeleton />
            ) : books.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {books.map((book) => (
                    <Grid item xs={6} sm={6} lg={3} key={book.id}>
                      <BookCard book={book} />
                    </Grid>
                  ))}
                </Grid>

                <Stack
                  direction="row"
                  justifyContent="center" 
                  sx={{ mt: 4, width: "100%", overflow: "hidden" }}
                >
                  <StyledPagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    isRtl={isRtl}
                    boundaryCount={1}
                    siblingCount={1}
                    dir={isRtl ? "rtl" : "ltr"}
                    shape="rounded"
                  />
                </Stack>
              </>
            ) : (
              <EmptyState />
            )}
          </Grid>
        </Grid>

        {/* Mobile Filter Bottom Sheet */}
        {showMobileFilter && (
          <FilterSection
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onClose={() => setShowMobileFilter(false)}
            isMobile
          />
        )}
      </Container>
    </Box>
  );
};

export default AudioBookList;
